import { Box, Container, Flex, Image, Input, Text } from "@chakra-ui/react"
import {
  Link as RouterLink,
  createFileRoute,
  redirect,
} from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"

import type { RegisterRequest } from "@/client"
import { Button } from "@/components/atoms/Button"
import { PublicHeader } from "@/components/layout/PublicHeader"
import useAuth, { isLoggedIn } from "@/hooks/useAuth"
import { confirmPasswordRules, emailPattern, passwordRules } from "@/utils"
import Logo from "/assets/images/dory-logo.svg"

export const Route = createFileRoute("/signup")({
  component: SignUp,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

interface UserRegisterForm extends RegisterRequest {
  confirm_password: string
}

function SignUp() {
  const { signUpMutation } = useAuth()
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UserRegisterForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      email: "",
      full_name: "",
      password: "",
      confirm_password: "",
    },
  })

  const onSubmit: SubmitHandler<UserRegisterForm> = (data) => {
    if (Object.keys(errors).length > 0) {
      return
    }
    signUpMutation.mutate(data)
  }

  return (
    <Box minH="100vh">
      <PublicHeader hideAuthButtons />
      <Flex
        flexDir={{ base: "column", md: "row" }}
        justify="center"
        h="calc(100vh - 64px)"
      >
        <Container
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          h="100%"
          maxW="sm"
          px={{ base: 4, md: 0 }}
          alignItems="stretch"
          justifyContent="center"
          gap={4}
          centerContent
        >
          <Image
            src={Logo}
            alt="Dory Swim School"
            height="auto"
            maxW={{ base: "200px", md: "xs" }}
            alignSelf="center"
            mb={4}
          />
          <Input
            id="full_name"
            minLength={3}
            {...register("full_name", {
              required: "Full Name is required",
            })}
            placeholder="Full Name"
            type="text"
          />
          {errors.full_name && (
            <Text color="red.500" fontSize="sm">
              {errors.full_name.message}
            </Text>
          )}

          <Input
            id="email"
            {...register("email", {
              required: "Email is required",
              pattern: emailPattern,
            })}
            placeholder="Email"
            type="email"
          />
          {errors.email && (
            <Text color="red.500" fontSize="sm">
              {errors.email.message}
            </Text>
          )}

          <Input
            type="password"
            {...register("password", passwordRules())}
            placeholder="Password"
          />
          {errors.password && (
            <Text color="red.500" fontSize="sm">
              {errors.password.message}
            </Text>
          )}

          <Input
            type="password"
            {...register("confirm_password", confirmPasswordRules(getValues))}
            placeholder="Confirm Password"
          />
          {errors.confirm_password && (
            <Text color="red.500" fontSize="sm">
              {errors.confirm_password.message}
            </Text>
          )}
          <Button
            variant="solid"
            type="submit"
            loading={isSubmitting}
            minH="44px"
          >
            Sign Up
          </Button>
          <Text>
            Already have an account?{" "}
            <RouterLink to="/login" className="main-link">
              Log In
            </RouterLink>
          </Text>
        </Container>
      </Flex>
    </Box>
  )
}

export default SignUp
